import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getAllUsers } from '../../services/firestoreService';

const UserExport = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState('');

  const isAdmin = hasRole('admin');

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          You don't have permission to access user export.
        </div>
      </div>
    );
  }

  const loadAllUsers = async () => {
    try {
      setLoading(true);
      setMessage('Loading all users...');

      // Get all users (increase limit to get everyone)
      const allUsers = await getAllUsers(1000);
      setUsers(allUsers);

      // Debug: Log all user emails to console
      console.log('🔍 All users in Firestore:');
      allUsers.forEach((user, index) => {
        console.log(`${index + 1}. Email: ${user.email}, Status: ${user.status}, Role: ${user.role}`);
      });

      setMessage(`✅ Loaded ${allUsers.length} users successfully! Check console for details.`);
    } catch (error) {
      console.error('Error loading users:', error);
      setMessage('❌ Error loading users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (users.length === 0) {
      setMessage('❌ No users to export. Please load users first.');
      return;
    }

    const headers = [
      'Email',
      'First Name', 
      'Last Name',
      'Display Name',
      'Company',
      'Job Title',
      'Phone',
      'LinkedIn URL',
      'Status',
      'Role',
      'Member Since',
      'Created At',
      'Last Login'
    ];

    const csvData = users.map(user => [
      user.email || '',
      user.firstName || '',
      user.lastName || '',
      user.displayName || '',
      user.company || '',
      user.jobTitle || '',
      user.phone || '',
      user.linkedinUrl || '',
      user.status || 'pending',
      user.role || 'member',
      user.memberSince ? new Date(user.memberSince.seconds * 1000).toLocaleDateString() : '',
      user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : '',
      user.lastLoginAt ? new Date(user.lastLoginAt.seconds * 1000).toLocaleDateString() : 'Never'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `APN_Users_Export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setMessage(`✅ Exported ${users.length} users to CSV file!`);
  };

  const exportToExcel = async () => {
    if (users.length === 0) {
      setMessage('❌ No users to export. Please load users first.');
      return;
    }

    try {
      // Dynamic import for xlsx
      const XLSX = await import('xlsx');
      
      const workbook = XLSX.utils.book_new();
      
      // Prepare data for Excel
      const excelData = users.map(user => ({
        'Email': user.email || '',
        'First Name': user.firstName || '',
        'Last Name': user.lastName || '',
        'Display Name': user.displayName || '',
        'Company': user.company || '',
        'Job Title': user.jobTitle || '',
        'Phone': user.phone || '',
        'LinkedIn URL': user.linkedinUrl || '',
        'Status': user.status || 'pending',
        'Role': user.role || 'member',
        'Member Since': user.memberSince ? new Date(user.memberSince.seconds * 1000).toLocaleDateString() : '',
        'Created At': user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : '',
        'Last Login': user.lastLoginAt ? new Date(user.lastLoginAt.seconds * 1000).toLocaleDateString() : 'Never',
        'About Me': user.aboutMe || ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'APN Users');

      // Auto-size columns
      const colWidths = Object.keys(excelData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      worksheet['!cols'] = colWidths;

      const filename = `APN_Users_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, filename);

      setMessage(`✅ Exported ${users.length} users to Excel file!`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setMessage('❌ Error exporting to Excel. CSV export is still available.');
    }
  };

  const clearUserLogins = async () => {
    const confirmClear = window.confirm(
      `⚠️ WARNING: This will clear login sessions for ALL ${users.length} users. They will need to log in again.\n\nAre you sure you want to proceed?`
    );

    if (!confirmClear) return;

    try {
      setLoading(true);
      setMessage('Clearing user login sessions...');

      // Note: Firebase Auth sessions are handled client-side
      // This would require Firebase Admin SDK to revoke tokens
      // For now, we'll show instructions for manual clearing

      setMessage(`
        ⚠️ To clear all user login sessions:
        
        1. Go to Firebase Console: https://console.firebase.google.com/project/the-apn/authentication/users
        2. Use the "Revoke refresh tokens" option for bulk operations
        3. Or use Firebase Admin SDK to revoke tokens programmatically
        
        Alternative: Users can be notified to log out and log back in.
      `);
    } catch (error) {
      console.error('Error clearing logins:', error);
      setMessage('❌ Error clearing login sessions: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkOrphanedAuthUsers = () => {
    setMessage(`
🔍 ORPHANED AUTH USERS DETECTED:

The emails "agourasn@gmail.com" and "cburnham@the-apn.com" exist in Firebase Auth but not in Firestore.

🔧 TO FIX THIS:
1. Go to Firebase Console: https://console.firebase.google.com/project/the-apn/authentication/users
2. Find users with these emails:
   - agourasn@gmail.com
   - cburnham@the-apn.com
3. Click the 3-dots menu → Delete user for each
4. Then try registering again

OR

Use completely different email addresses for registration like:
- test@example.com
- newuser@gmail.com
- yourname+test@gmail.com

This mismatch happens when Firebase Auth users exist but their Firestore documents were deleted.
    `);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">User Export & Management</h1>
        
        {/* Load Users */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Load User Data</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700 mb-4">
              Load all users from the database to export or manage their accounts.
            </p>
            <button
              onClick={loadAllUsers}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : `Load All Users (${users.length} loaded)`}
            </button>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`p-4 rounded-md mb-6 whitespace-pre-line ${
            message.includes('✅') ? 'bg-green-100 text-green-700' : 
            message.includes('❌') ? 'bg-red-100 text-red-700' : 
            message.includes('⚠️') ? 'bg-yellow-100 text-yellow-700' :
            'bg-blue-100 text-blue-700'
          }`}>
            {message}
          </div>
        )}

        {/* Export Options */}
        {users.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Export User Data</h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-4">
                Export user data for communication and membership management.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={exportToCSV}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  📄 Export to CSV
                </button>
                <button
                  onClick={exportToExcel}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  📊 Export to Excel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* User Management */}
        {users.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">User Management</h2>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-yellow-800 mb-4">
                ⚠️ <strong>Clear Login Sessions:</strong> Force all users to log in again.
              </p>
              <button
                onClick={clearUserLogins}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                🔄 Clear All Login Sessions
              </button>
            </div>
          </div>
        )}

        {/* Registration Issues */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Registration Issues</h2>
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-red-800 mb-4">
              🔍 <strong>Check for Orphaned Auth Users:</strong> Find users in Firebase Auth but not in Firestore.
            </p>
            <button
              onClick={checkOrphanedAuthUsers}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              🔍 Check agourasn@gmail.com Issue
            </button>
          </div>
        </div>

        {/* User Summary */}
        {users.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">User Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900">Total Users</h3>
                <p className="text-3xl font-bold text-blue-600">{users.length}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900">Approved Members</h3>
                <p className="text-3xl font-bold text-green-600">
                  {users.filter(u => u.status === 'approved').length}
                </p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-yellow-900">Pending Approval</h3>
                <p className="text-3xl font-bold text-yellow-600">
                  {users.filter(u => u.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2">Next Steps:</h4>
          <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
            <li>Load all users to see current member list</li>
            <li>Export user data to CSV or Excel for communication</li>
            <li>Use exported data to send membership upgrade emails</li>
            <li>Clear login sessions if needed to force re-authentication</li>
            <li>Monitor user activity and membership status</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default UserExport;
